import { Room, Player, RoomAndPlayerUsername } from '../Types'
import { PlayingCard, CardName } from '../Cards'

// Backend API base URL - update this based on your environment
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001'

/**
 * API Client for backend communication
 */
class ApiClient {
    private baseUrl: string

    constructor(baseUrl: string = API_BASE_URL) {
        this.baseUrl = baseUrl
    }

    /**
     * Generic fetch wrapper with error handling
     */
    private async fetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
        const url = `${this.baseUrl}${endpoint}`

        try {
            const response = await fetch(url, {
                ...options,
                headers: {
                    'Content-Type': 'application/json',
                    ...options?.headers,
                },
            })

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}))
                throw new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`)
            }

            return await response.json()
        } catch (error) {
            console.error(`API Error [${endpoint}]:`, error)
            throw error
        }
    }

    /**
     * Health check
     */
    async healthCheck(): Promise<{ status: string; timestamp: string; uptime: number }> {
        return this.fetch('/health')
    }

    /**
     * Create a new room
     */
    async createRoom(): Promise<{ gameId: string; room: Room }> {
        return this.fetch('/api/rooms', {
            method: 'POST',
        })
    }

    /**
     * Get room details
     */
    async getRoom(gameId: string): Promise<{ room: Room }> {
        return this.fetch(`/api/rooms/${gameId}`)
    }

    /**
     * Check if room exists
     */
    async roomExists(gameId: string): Promise<{ exists: boolean }> {
        return this.fetch(`/api/rooms/${gameId}/exists`)
    }

    /**
     * Delete a room
     */
    async deleteRoom(gameId: string): Promise<{ success: boolean }> {
        return this.fetch(`/api/rooms/${gameId}`, {
            method: 'DELETE',
        })
    }

    /**
     * Add player to room
     */
    async addPlayer(gameId: string, username: string): Promise<RoomAndPlayerUsername> {
        return this.fetch(`/api/rooms/${gameId}/players`, {
            method: 'POST',
            body: JSON.stringify({ username }),
        })
    }

    /**
     * Remove player from room
     */
    async removePlayer(gameId: string, username: string): Promise<{ success: boolean }> {
        return this.fetch(`/api/rooms/${gameId}/players/${username}`, {
            method: 'DELETE',
        })
    }

    /**
     * Set player ready status
     */
    async setPlayerReady(gameId: string, username: string, ready: boolean): Promise<{ success: boolean }> {
        return this.fetch(`/api/rooms/${gameId}/players/${username}/ready`, {
            method: 'PATCH',
            body: JSON.stringify({ ready }),
        })
    }

    /**
     * Start game
     */
    async startGame(gameId: string): Promise<{ room: Room }> {
        return this.fetch(`/api/rooms/${gameId}/start`, {
            method: 'POST',
        })
    }

    /**
     * Call bluff
     */
    async callBluff(gameId: string, playerId: string): Promise<{ success: boolean; message?: string }> {
        return this.fetch(`/api/rooms/${gameId}/bluff`, {
            method: 'POST',
            body: JSON.stringify({ playerId }),
        })
    }

    /**
     * Move cards after bluff
     */
    async moveCards(gameId: string, showCards: boolean = false): Promise<{ success: boolean }> {
        return this.fetch(`/api/rooms/${gameId}/move-cards`, {
            method: 'POST',
            body: JSON.stringify({ showCards }),
        })
    }

    /**
     * Process next turn
     */
    async processNextTurn(gameId: string): Promise<{ room: Room }> {
        return this.fetch(`/api/rooms/${gameId}/next-turn`, {
            method: 'POST',
        })
    }

    /**
     * Play cards
     */
    async playCards(
        gameId: string,
        playerId: string,
        cards: PlayingCard[],
        roundValue?: CardName
    ): Promise<{ room: Room }> {
        return this.fetch(`/api/rooms/${gameId}/play`, {
            method: 'POST',
            body: JSON.stringify({ playerId, cards, roundValue }),
        })
    }

    /**
     * Pass turn
     */
    async passTurn(gameId: string, playerId: string): Promise<{ success: boolean }> {
        return this.fetch(`/api/rooms/${gameId}/pass`, {
            method: 'POST',
            body: JSON.stringify({ playerId }),
        })
    }

    /**
     * Get SSE stream URL for a room
     */
    getStreamUrl(gameId: string, playerId?: string): string {
        const url = `${this.baseUrl}/api/rooms/${gameId}/stream`
        return playerId ? `${url}?playerId=${playerId}` : url
    }
}

// Export singleton instance
export const apiClient = new ApiClient()
export default apiClient
